import { useCallback, useId, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { useAtomValue } from 'jotai';
import { HeartIcon } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import { TileOverlay } from '@rocco/ui/tile-shared/components/TileOverlay';
import { FollowSelector } from './FollowSelector';
import { useFollowSelectorContext } from '../context/FollowSelectorContext';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import { useGuestSave } from '~/modules/moodboards/hooks/use-guest-save';
import { isUserLoggedIn, useUser } from '@modules/user';
import type { PinItemType } from '~/modules/moodboards/type';
import { lastUsedBoardIdAtom } from '~/modules/moodboards/state/design-board-save-state';
import { buildThumbnail } from '../utils/build-sections';
import { resolveFixedOffset } from '../utils/fixed-containing-block';
import { useTracker } from '@archipro-website/tracker';
import { useFeature } from '~/modules/root';
import { useMatchesHandleData } from '~/modules/root/hook';
import {
    TileActionPanel,
    type TileActionInfo,
    type TilePinterestConfig,
    type TileVisitWebsiteConfig,
} from './TileActionPanel';
import type { ShareOptions } from '~/modules/social-sharing/utils/sharing';
import type { TileEnquiryConfig } from '~/modules/enquiry/hooks/use-tile-enquiry';

interface TileOverlayWithFollowSelectorProps {
    /** The item ID to save. */
    pinId: number;
    /** The library item ID. Falls back to pinId if not provided. */
    libraryItemId?: number;
    /** The pin type (e.g. 'ProductPin', 'ProjectPin'). */
    pinType: PinItemType;
    /** Whether the overlay actions are disabled. */
    disabled?: boolean;
    /** Render a smaller heart button on mobile (for compact tile contexts like recently viewed). */
    compact?: boolean;
    /**
     * Extra classes for the desktop overlay surface — e.g. `rounded-none` to
     * square off the default `rounded-tile` corners on sharp editorial tiles.
     */
    overlayClassName?: string;
    /**
     * Extra classes for the mobile heart button — e.g. repositioning it away
     * from the default top-right corner when something else already sits there.
     */
    mobileButtonClassName?: string;
    /**
     * Fires just before a signed-out visitor is sent to the auth modal.
     *
     * That modal is the legacy aria dialog, which is not a Radix dismissable
     * layer — so when this overlay lives inside a modal Radix dialog, the
     * modal renders but `pointer-events: none` on the body leaves it dead.
     * Hosts in that position use this to step out of the way first.
     */
    onGuestAuthRequired?: () => void;
    /**
     * Treat this as a touch surface at every width: keep the panel (or the
     * legacy heart) here on touch rather than deferring to a sibling slot, and
     * never mount the desktop hover overlay past the `md:` break.
     *
     * For hosts that only render on a mobile device (decided by user agent,
     * not viewport). A phone in landscape is wider than 768px, where the
     * default split would swap the heart for an overlay that is `inset-0`,
     * `opacity-0` and hit-testable — an invisible sheet over the tile that
     * cannot be hovered away on touch and swallows taps and gestures.
     */
    mobileOnly?: boolean;
    /** Round the overlay corners to match the tile. Defaults to `true`. */
    rounded?: boolean;
    /**
     * Where the mobile `•••` sits inside the tile, as position utilities.
     * Defaults to the picture's top right.
     */
    triggerClassName?: string;
    /** Draw the mobile `•••` as the glyph alone, with no disc behind it. */
    bareTrigger?: boolean;
    /**
     * Enquiry config for the panel's Enquire button.
     * Provide for product / professional / project tiles to show the primary
     * Enquire button; omit for a More-only panel (article / category).
     */
    enquiry?: TileEnquiryConfig | null;
    /** Label for the enquiry button (flag on). Defaults to "Enquire". */
    enquiryLabel?: string;
    /**
     * Website target for the "Visit site" action (flag on). Omit / null when the
     * item has no website, or on surfaces that don't fetch one.
     */
    visitWebsite?: TileVisitWebsiteConfig | null;
    /** Share target for the Share action. Omit to hide Share. */
    share?: ShareOptions | null;
    /**
     * Overrides for the panel's Pinterest action, for a host whose picture is
     * not the tile's own thumbnail — a gallery slide pins the photograph on
     * screen rather than the product's first image.
     */
    pinterest?: TilePinterestConfig | null;
    /** Item info for the mobile action sheet header. */
    tileInfo?: TileActionInfo;
    /**
     * Keep the legacy board-dropdown overlay instead of the action panel.
     *
     * One surface asks for this: the photo viewer, whose control is chrome
     * inside a Radix dialog and whose signed-out Save hands over to the legacy
     * auth modal, which cannot live inside that dialog.
     */
    followOverlayOnly?: boolean;
}

/**
 * Whether this tile shows the action panel rather than the legacy board
 * dropdown: `revamp_2026` is on, and the host has not asked to keep the
 * old overlay.
 *
 * The flag decides new against old for the whole platform, so the in-link
 * overlay and the sibling mobile slot have to resolve to one answer.
 */
export const useTileActionPanelEnabled = (
    followOverlayOnly?: boolean,
): boolean => {
    const flagOn = useFeature('revamp_2026');
    const [searchParams] = useSearchParams();
    // Dev-only preview, so the panel can be seen before the flag row exists in
    // an environment's DB (mirrors the gallery's `?detailsRevamp=1`).
    const devPreview =
        import.meta.env.DEV && searchParams.get('tileEnquiry') === '1';

    return !followOverlayOnly && (flagOn || devPreview);
};

/**
 * The tile's hover actions: the platform action panel while
 * `revamp_2026` is on, otherwise the legacy board-dropdown overlay.
 *
 * On mobile the panel is rendered as a sibling of the tile link by
 * TileMobileActionsSlot, so this returns null there.
 */
export const TileOverlayWithFollowSelector = (
    props: TileOverlayWithFollowSelectorProps,
) => {
    // BM website preview is a locked picture of the page — Save / Visit /
    // Share on tiles would still paint on hover and punch through the
    // preview's pointer-events lock (BC-5305). Directory tiles already omit
    // this overlay in preview; detail-page tiles go through here.
    const isBMPreview = useMatchesHandleData('isBMPreview', false);
    const panelEnabled = useTileActionPanelEnabled(props.followOverlayOnly);
    const { isMobile } = useAtomValue(uiContextAtomDisplayMode);
    if (isBMPreview) return null;
    if (panelEnabled) {
        // Mobile renders the `•••` trigger + sheet as a sibling of the tile link
        // (TileMobileActionsSlot), so a tap can never fall through to the anchor.
        // Nothing goes inside the in-link overlay on mobile.
        //
        // `mobileOnly` says there is no such link to fall through to — the host
        // is a gallery slide or a viewer, not a tile wrapped in an anchor — so
        // it keeps the panel here rather than waiting for a sibling that the
        // host never mounts.
        if (isMobile && !props.mobileOnly) return null;
        return (
            <TileActionPanel
                pinId={props.pinId}
                libraryItemId={props.libraryItemId}
                pinType={props.pinType}
                enquiry={props.enquiry}
                enquiryLabel={props.enquiryLabel}
                visitWebsite={props.visitWebsite}
                share={props.share}
                pinterest={props.pinterest}
                tileInfo={props.tileInfo}
                disabled={props.disabled}
                rounded={props.rounded}
            />
        );
    }
    return <FollowSelectorOverlay {...props} />;
};

/**
 * The mobile action panel (`•••` → bottom sheet) rendered as an absolutely
 * positioned sibling of the tile link, so taps on it are structurally isolated
 * from the anchor — no pre-hydration tap-through, no backdrop-dismiss ghost
 * navigation. Renders nothing on desktop, or for a host that kept the legacy
 * overlay; desktop keeps the in-link hover bar from
 * TileOverlayWithFollowSelector.
 */
export const TileMobileActionsSlot = (
    props: TileOverlayWithFollowSelectorProps,
) => {
    // Same lock as TileOverlayWithFollowSelector — no mobile Save sheet in
    // the BM website preview (BC-5305).
    const isBMPreview = useMatchesHandleData('isBMPreview', false);
    const panelEnabled = useTileActionPanelEnabled(props.followOverlayOnly);
    const { isMobile } = useAtomValue(uiContextAtomDisplayMode);
    if (isBMPreview || !panelEnabled || !isMobile) return null;
    return (
        <TileActionPanel
            pinId={props.pinId}
            libraryItemId={props.libraryItemId}
            pinType={props.pinType}
            enquiry={props.enquiry}
            enquiryLabel={props.enquiryLabel}
            visitWebsite={props.visitWebsite}
            share={props.share}
            pinterest={props.pinterest}
            tileInfo={props.tileInfo}
            disabled={props.disabled}
            rounded={props.rounded}
            triggerClassName={props.triggerClassName}
            bareTrigger={props.bareTrigger}
        />
    );
};

const FollowSelectorOverlay = ({
    pinId,
    libraryItemId,
    pinType,
    disabled = false,
    compact = false,
    overlayClassName,
    mobileButtonClassName,
    onGuestAuthRequired,
    mobileOnly = false,
    rounded = true,
    tileInfo,
}: TileOverlayWithFollowSelectorProps) => {
    const instanceId = useId();
    const tracker = useTracker();
    const user = useUser();
    const guestID =
        user.__typename === 'Me' ? user.TrackedGuest?.ID : undefined;
    const isLoggedIn = isUserLoggedIn(user);
    const { guestSave } = useGuestSave();
    const { isInDesignBoard, handleSaveToBoard, handleRemoveFromBoard } =
        useHandleFollow();
    const { openFollowSelector: contextOpen, openSelectorId } =
        useFollowSelectorContext();
    const {
        getSections,
        boards: rawBoards,
        moveToTop,
        getSavedBoardIdsForPin,
        ensureMembershipLoaded,
        isLoading: boardsLoading,
    } = useFollowSelectorContext();
    const anchorRef = useRef<HTMLSpanElement>(null);
    const dropdownButtonRef = useRef<HTMLButtonElement>(null);
    const lastUsedBoardId = useAtomValue(lastUsedBoardIdAtom);

    /** Whether this instance's FollowSelector popover is currently open. */
    const isFollowSelectorOpen = openSelectorId === instanceId;

    const isSaved = isInDesignBoard(pinId, pinType);

    const selectedItemIds = useMemo(
        () => [libraryItemId ?? pinId],
        [libraryItemId, pinId],
    );

    /**
     * Resolve the latest (most recently used) board from the sections.
     * Boards are sorted by LastActivity DESC, so the first board is the latest.
     *
     * Uses `getSections()` for the actual selection logic (popover interactions),
     * but the trigger label (defaultBoardName) is derived from `rawBoards`
     * so it stays stable during search.
     */
    const getLatestBoard = useCallback(() => {
        const sections = getSections();
        for (const section of sections) {
            if (section.items.length > 0) {
                return section.items[0];
            }
        }
        return null;
    }, [getSections]);

    /**
     * Resolve the board title shown in the overlay dropdown trigger.
     * - Guest → static "Save to Design Board".
     * - Logged-in, boards still loading → undefined (triggers shimmer).
     * - Logged-in, boards loaded but empty (new user) → "Save to Design Board".
     * - Logged-in, boards available → actual board title.
     */
    const defaultBoardName = useMemo(() => {
        if (!isLoggedIn) {
            return 'Save to Design Board';
        }

        const allBoards = getSections().flatMap((section) => section.items);

        if (lastUsedBoardId) {
            const lastUsedBoard = allBoards.find(
                (board) => board.id === lastUsedBoardId,
            );
            if (lastUsedBoard) {
                return lastUsedBoard.title;
            }
        }

        const latestTitle = getLatestBoard()?.title;
        if (latestTitle) return latestTitle;

        if (!boardsLoading) return 'Save to Design Board';
        return undefined;
    }, [
        isLoggedIn,
        getSections,
        lastUsedBoardId,
        getLatestBoard,
        boardsLoading,
    ]);

    /**
     * Position the anchor span at the target element's viewport location,
     * correcting for any ancestor that captures fixed descendants.
     */
    const positionAnchorAt = useCallback((element: HTMLElement) => {
        const anchor = anchorRef.current;
        if (!anchor) return;
        const targetRect = element.getBoundingClientRect();
        const { top, left } = resolveFixedOffset(anchor, targetRect);

        anchor.style.top = `${top}px`;
        anchor.style.left = `${left}px`;
        anchor.style.width = `${targetRect.width}px`;
        anchor.style.height = `${targetRect.height}px`;
    }, []);

    /**
     * Quick-save or quick-unsave the pin.
     * - Not saved → save to the latest (most recently used) board.
     * - Already saved → remove from the latest board.
     * For guest users, shows the auth modal so they can log in first.
     */
    const handleQuickSave = useCallback(
        async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            e.stopPropagation();

            if (!isLoggedIn) {
                onGuestAuthRequired?.();
                guestSave({
                    pinId,
                    pinType,
                    libraryItemId,
                    title: tileInfo?.title,
                    image: tileInfo?.paneImage ?? tileInfo?.image,
                });
                return;
            }

            if (isSaved) {
                // Quick-unsave: remove from the specific board this pin belongs to.
                let savedBoardIds = getSavedBoardIdsForPin(pinId, pinType);
                if (savedBoardIds.length === 0) {
                    savedBoardIds = await ensureMembershipLoaded(
                        pinId,
                        pinType,
                    );
                }

                if (savedBoardIds.length === 0) {
                    return;
                }

                const targetBoardId =
                    lastUsedBoardId && savedBoardIds.includes(lastUsedBoardId)
                        ? lastUsedBoardId
                        : savedBoardIds[0];

                const targetBoardFromSections = getSections()
                    .flatMap((section) => section.items)
                    .find((item) => item.id === targetBoardId);

                const targetBoardFromRawBoards = rawBoards.find(
                    (board) => board.id === targetBoardId,
                );

                const targetBoard =
                    targetBoardFromSections ??
                    (targetBoardFromRawBoards
                        ? {
                              id: targetBoardFromRawBoards.id,
                              title: targetBoardFromRawBoards.title,
                              projectName:
                                  targetBoardFromRawBoards.project?.title,
                              thumbnailUrls: targetBoardFromRawBoards.pins
                                  .map((pin) => pin.image)
                                  .filter(Boolean),
                              thumbnail: buildThumbnail(
                                  targetBoardFromRawBoards,
                              ),
                              entityType: 'moodboard' as const,
                              urlSegment: targetBoardFromRawBoards.urlSegment,
                              projectId: targetBoardFromRawBoards.project?.id,
                              projectUrlSegment:
                                  targetBoardFromRawBoards.project?.urlSegment,
                              isPersonal:
                                  targetBoardFromRawBoards.isPersonalMoodBoard,
                          }
                        : null);

                if (!targetBoard) {
                    return;
                }

                handleRemoveFromBoard(
                    targetBoard,
                    { id: pinId, libraryItemId },
                    pinType,
                );
                return;
            }

            const latestBoard =
                (lastUsedBoardId
                    ? getSections()
                          .flatMap((section) => section.items)
                          .find((item) => item.id === lastUsedBoardId)
                    : null) ?? getLatestBoard();

            if (!latestBoard) {
                // No boards available — open the selector at the dropdown trigger
                // so it appears in the same position as a regular dropdown click.
                if (dropdownButtonRef.current) {
                    positionAnchorAt(dropdownButtonRef.current);
                }
                contextOpen(instanceId, {
                    selectedItemIds,
                    selectedItemType: pinType,
                });
                return;
            }

            // Quick-save: add to the latest board
            handleSaveToBoard(
                latestBoard,
                { id: pinId, libraryItemId },
                pinType,
            );
            const itemId = libraryItemId ?? pinId;
            void tracker.log('SaveSubmit', {
                url: new URL(window.location.href),
                data: {
                    LibraryItemID: itemId,
                    ...(guestID ? { guest_id: guestID } : {}),
                },
            });
            moveToTop(latestBoard.id);
        },
        [
            tracker,
            isLoggedIn,
            isSaved,
            guestSave,
            onGuestAuthRequired,
            pinId,
            pinType,
            libraryItemId,
            tileInfo,
            getLatestBoard,
            getSections,
            contextOpen,
            instanceId,
            selectedItemIds,
            handleSaveToBoard,
            handleRemoveFromBoard,
            moveToTop,
            getSavedBoardIdsForPin,
            ensureMembershipLoaded,
            rawBoards,
            lastUsedBoardId,
            guestID,
            positionAnchorAt,
        ],
    );

    const handleOverlayMouseEnter = useCallback(() => {
        if (!isLoggedIn || !isSaved) {
            return;
        }
        void ensureMembershipLoaded(pinId, pinType);
    }, [isLoggedIn, isSaved, ensureMembershipLoaded, pinId, pinType]);

    /**
     * Open the FollowSelector popover when the board dropdown is clicked.
     * For guest users, shows the auth modal so they can log in first.
     */
    const handleDropdownClick = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            e.stopPropagation();

            if (!isLoggedIn) {
                onGuestAuthRequired?.();
                guestSave({
                    pinId,
                    pinType,
                    libraryItemId,
                    title: tileInfo?.title,
                    image: tileInfo?.paneImage ?? tileInfo?.image,
                });
                return;
            }

            const libraryItemID = libraryItemId ?? pinId;
            tracker.log('PinMenuAdd', {
                url: new URL(window.location.href),
                data: {
                    LibraryItemID: libraryItemID,
                },
            });

            contextOpen(instanceId, {
                selectedItemIds,
                selectedItemType: pinType,
            });

            // Position the anchor at the clicked button
            positionAnchorAt(e.currentTarget);
        },
        [
            isLoggedIn,
            guestSave,
            onGuestAuthRequired,
            pinId,
            libraryItemId,
            tileInfo,
            tracker,
            contextOpen,
            instanceId,
            selectedItemIds,
            pinType,
            positionAnchorAt,
        ],
    );

    return (
        <>
            {/* Mobile: heart button that opens the full-screen drawer */}
            <button
                type="button"
                disabled={disabled}
                onClick={handleDropdownClick}
                // Icon-only, so it carries its own name. Matches the desktop
                // overlay's visible Save/Saved wording, which is what a voice
                // user would say out loud.
                aria-label={isSaved ? 'Saved' : 'Save'}
                className={cn(
                    'absolute top-3.5 right-4 z-10 flex items-center justify-center bg-white/70 backdrop-blur-xs',
                    !mobileOnly && 'md:hidden',
                    // Corner comes with the size: a radius at half the height
                    // would clamp back to the disc this replaced.
                    compact
                        ? 'size-8 rounded-[var(--radius-btn-s)]'
                        : 'size-[2.625rem] rounded-[var(--radius-btn-m)]',
                    // Extend the tap target to ≥44px via an invisible pseudo — the
                    // visual control stays 42px (WCAG 2.5.8 target size).
                    !compact &&
                        "before:absolute before:top-1/2 before:left-1/2 before:size-11 before:-translate-x-1/2 before:-translate-y-1/2 before:content-['']",
                    mobileButtonClassName,
                )}
            >
                <HeartIcon
                    aria-hidden="true"
                    className={cn(
                        compact ? 'size-3.5' : 'size-4',
                        isSaved
                            ? 'fill-gray-900 stroke-gray-900'
                            : 'stroke-gray-900',
                    )}
                />
            </button>

            {/* Desktop: Pinterest-style overlay */}
            {!mobileOnly && (
                <span onMouseEnter={handleOverlayMouseEnter}>
                    <TileOverlay
                        defaultBoardName={defaultBoardName}
                        boardNameLoading={isLoggedIn && !defaultBoardName}
                        isSaved={isSaved}
                        onSave={handleQuickSave}
                        onDropdownClick={handleDropdownClick}
                        dropdownRef={dropdownButtonRef}
                        disabled={disabled}
                        hideDropdownChevron={!isLoggedIn}
                        forceVisible={isFollowSelectorOpen}
                        rounded={rounded}
                        className={overlayClassName}
                    />
                </span>
            )}
            <FollowSelector
                instanceId={instanceId}
                anchorRef={anchorRef}
                pinId={pinId}
                libraryItemId={libraryItemId}
                pinType={pinType}
                popoverAlign="start"
            />
        </>
    );
};
